import React, { useState } from 'react';
import { useGame } from '../context/GameContext';

const CATEGORY_ICONS = { Faith: '🙏', Family: '🏠', Finances: '💰', Formation: '📚', Fitness: '💪', Fun: '🎮' };

export default function WeekSummary() {
  const { intentions, coachAdvice, coachAdviceFollowed, getCoachAdvice, followCoachAdvice, player, showNotification } = useGame();
  const [loading, setLoading] = useState(false);

  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  const weekIntentions = intentions.filter(i => new Date(i.createdAt) > weekAgo);

  const CATEGORIES = ['Faith', 'Family', 'Finances', 'Formation', 'Fitness', 'Fun'];
  const stats = CATEGORIES.map(cat => {
    const catItems = weekIntentions.filter(i => i.category === cat);
    return { cat, total: catItems.length, done: catItems.filter(i => i.completed).length };
  });

  const totalDone = weekIntentions.filter(i => i.completed).length;
  const totalAll = weekIntentions.length;

  const handleGetAdvice = async () => {
    setLoading(true);
    try {
      await getCoachAdvice();
    } catch {
      showNotification('Coach is offline. Try again.', 'error');
    }
    setLoading(false);
  };

  const handleFollow = async (category) => {
    await followCoachAdvice(category);
    showNotification(`Coach advice followed! Future intentions in ${category} earn ×2 XP!`, 'success');
  };

  return (
    <div className="page coach-page">
      <h1 className="page-title">WEEK SUMMARY & COACH</h1>

      {/* Week Stats */}
      <div className="stats-panel">
        <div className="stats-header">THIS WEEK'S BATTLE REPORT</div>
        <div className="stats-overview">
          <div className="stat-big">
            <span className="stat-num">{totalDone}</span>
            <span className="stat-lbl">COMPLETED</span>
          </div>
          <div className="stat-big">
            <span className="stat-num">{totalAll}</span>
            <span className="stat-lbl">TOTAL SET</span>
          </div>
          <div className="stat-big">
            <span className="stat-num">{totalAll > 0 ? Math.round((totalDone / totalAll) * 100) : 0}%</span>
            <span className="stat-lbl">COMPLETION</span>
          </div>
          <div className="stat-big">
            <span className="stat-num">LV{player.level}</span>
            <span className="stat-lbl">CURRENT LEVEL</span>
          </div>
        </div>
        <div className="category-stats">
          {stats.map(({ cat, total, done }) => (
            <div key={cat} className="cat-stat">
              <span className="cat-stat-icon">{CATEGORY_ICONS[cat]}</span>
              <span className="cat-stat-name">{cat}</span>
              <div className="cat-stat-bar">
                <div
                  className="cat-stat-fill"
                  style={{ width: total > 0 ? `${(done / total) * 100}%` : '0%' }}
                />
              </div>
              <span className="cat-stat-num">{done}/{total}</span>
              {total === 0 && <span className="cat-neglected">NEGLECTED</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Coach Panel */}
      <div className="coach-panel">
        <div className="coach-avatar">
          <div className="avatar-ring">
            <div className="avatar-face">🧠</div>
          </div>
          <div className="coach-name">COACH AI</div>
          <div className="coach-subtitle">POWERED BY GROQ</div>
        </div>

        {!coachAdvice && (
          <div className="coach-empty">
            <p className="coach-prompt-text">Ready to get your weekly battle analysis?</p>
            <button className="btn-primary" onClick={handleGetAdvice} disabled={loading}>
              {loading ? (
                <><span className="spinner" /> ANALYZING YOUR WEEK...</>
              ) : '⚡ GET COACH ANALYSIS'}
            </button>
          </div>
        )}

        {coachAdvice && (
          <div className="coach-content">
            {/* Motivation */}
            <div className="coach-motivation">
              <div className="coach-section-label">⚡ COACH SAYS</div>
              <p className="motivation-text">"{coachAdvice.motivation}"</p>
              {coachAdvice.weekGoal && (
                <p className="week-goal">🎯 THIS WEEK: {coachAdvice.weekGoal}</p>
              )}
            </div>

            {/* Strong areas */}
            {coachAdvice.strongAreas?.length > 0 && (
              <div className="coach-strong">
                <div className="coach-section-label">🏆 WHAT YOU'RE CRUSHING</div>
                {coachAdvice.strongAreas.map((area, i) => (
                  <div key={i} className="strong-card">
                    <span className="strong-cat">{CATEGORY_ICONS[area.category]} {area.category}</span>
                    <p className="strong-text">{area.praise}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Neglected areas with advice */}
            {coachAdvice.neglectedAdvice?.length > 0 && (
              <div className="coach-neglected">
                <div className="coach-section-label">⚠ AREAS NEEDING ATTENTION</div>
                {coachAdvice.neglectedAdvice.map((item, i) => {
                  const followed = coachAdviceFollowed.includes(item.category);
                  return (
                    <div key={i} className={`advice-card ${followed ? 'advice-followed' : ''}`}>
                      <div className="advice-header">
                        <span className="advice-cat">{CATEGORY_ICONS[item.category]} {item.category}</span>
                        {followed && <span className="followed-badge">✓ FOLLOWED — ×2 XP ACTIVE</span>}
                      </div>
                      <p className="advice-text">{item.advice}</p>
                      {item.penalty && <p className="advice-penalty">⚠ {item.penalty}</p>}
                      {!followed && (
                        <button className="btn-follow" onClick={() => handleFollow(item.category)}>
                          ✓ I WILL FOLLOW THIS ADVICE (+2× XP)
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <button className="btn-secondary" onClick={handleGetAdvice} disabled={loading}>
              {loading ? 'ANALYZING...' : '🔄 REFRESH ANALYSIS'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
